import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Award, DollarSign, Download, Search, ShoppingBag, TrendingUp, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import FiltroPeriodo from "../components/FiltroPeriodo";
import { CardResumo, Vazio } from "../components/Estados";
import { Esmaecer, EsqueletoClientes } from "../components/Esqueletos";
import { buscarDetalhado } from "../lib/api";
import { exportarExcel } from "../lib/excel";
import { formatCurrency, formatInt } from "../lib/formato";
import { rotuloArquivo } from "../lib/periodo";
import { useDados, usePeriodo } from "../lib/usePeriodo";

const MEDALHA = ["text-yellow-500", "text-gray-400", "text-orange-500"];
const dataBR = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");
const COLUNAS_EXCEL = [
  { header: "#", value: (_, i) => i + 1 },
  { header: "Cliente", value: (c) => c.nome },
  { header: "Pedidos", value: (c) => c.quantidade_pedidos },
  { header: "Valor total (R$)", value: (c) => c.valor_total },
  { header: "Ticket médio (R$)", value: (c) => c.ticket_medio },
  { header: "Último pedido", value: (c) => dataBR(c.ultimo_pedido) },
];
const carregarClientes = (periodo) => buscarDetalhado("clientes", periodo);

function Tabela({ lista, total, aoAbrir }) {
  const th = "py-2 px-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold";
  return (
    <table className="w-full text-sm" data-e2e="tabela-clientes">
      <thead className="bg-muted/40">
        <tr>
          <th className={`text-left ${th} w-10`}>#</th><th className={`text-left ${th}`}>Cliente</th>
          <th className={`text-right ${th}`}>Pedidos</th><th className={`text-right ${th}`}>Valor total</th>
          <th className={`text-right ${th}`}>Ticket médio</th><th className={`text-right ${th}`}>% do total</th>
          <th className={`text-right ${th} pr-4`}>Último pedido</th>
        </tr>
      </thead>
      <tbody>
        {lista.map((c) => (
          <tr key={c.id} className="border-t hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => aoAbrir(c)} title="Abrir os pedidos deste cliente">
            <td className="py-2 px-2">{c.posicao <= 3 ? <Award className={`w-4 h-4 ${MEDALHA[c.posicao - 1]}`} /> : <span className="text-muted-foreground tabular-nums">{c.posicao}</span>}</td>
            <td className="py-2 px-2 font-medium text-primary hover:underline">{c.nome}</td>
            <td className="py-2 px-2 text-right tabular-nums">{formatInt(c.quantidade_pedidos)}</td>
            <td className="py-2 px-2 text-right font-semibold tabular-nums text-green-600 dark:text-green-400">{formatCurrency(c.valor_total)}</td>
            <td className="py-2 px-2 text-right tabular-nums">{formatCurrency(c.ticket_medio)}</td>
            <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">{total > 0 ? ((c.valor_total / total) * 100).toFixed(1).replace(".", ",") : "0"}%</td>
            <td className="py-2 px-2 pr-4 text-right tabular-nums text-muted-foreground">{dataBR(c.ultimo_pedido)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Relatório de Vendas → Top Clientes → "Ver todos": todos os clientes identificados
// do período, na ordem do Top 5 (achado 77 — antes a área ficava em branco).
// Abre com o período que estava no Relatório de Vendas.
export default function ClientesTab({ definirAtualizar, periodoInicial, onVoltar }) {
  const navigate = useNavigate();
  const p = usePeriodo(periodoInicial);
  const { dados, carregando, carregou, recarregar } = useDados(carregarClientes, p.periodo, { rotulo: "os clientes" });
  const [busca, setBusca] = useState("");
  useEffect(() => { definirAtualizar({ recarregar, carregando }); }, [definirAtualizar, recarregar, carregando]);

  const todos = useMemo(() => (dados?.clientes || []).map((c, i) => ({ ...c, posicao: i + 1 })), [dados]);
  const termo = busca.trim().toLowerCase();
  const lista = termo ? todos.filter((c) => (c.nome || "").toLowerCase().includes(termo)) : todos;
  const total = dados?.total_valor || 0;
  const pedidos = todos.reduce((s, c) => s + (c.quantidade_pedidos || 0), 0);
  const abrir = (c) => navigate(`/admin/vendas/pedidos?cliente=${encodeURIComponent(c.nome)}`);
  return (
    <div className="space-y-6">
      <button type="button" onClick={() => onVoltar({ filtro: p.filtro, dataInicio: p.dataInicio, dataFim: p.dataFim })} data-e2e="voltar-vendas"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" />Voltar ao Relatório de Vendas
      </button>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0"><FiltroPeriodo {...p} /></div>
        {todos.length > 0 && (
          <Button variant="outline" size="sm" className="gap-2 shrink-0"
            onClick={() => exportarExcel(todos, COLUNAS_EXCEL, `Clientes_${rotuloArquivo(p.periodo)}.xlsx`)}>
            <Download className="w-4 h-4" /> Excel
          </Button>
        )}
      </div>
      {!carregou ? <EsqueletoClientes /> : (
        <Esmaecer ativo={carregando}>
          {!todos.length ? <Vazio texto="Nenhum cliente identificado comprou no período" /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <CardResumo label="Clientes" value={formatInt(todos.length)} icon={Users} />
            <CardResumo label="Pedidos" value={formatInt(pedidos)} icon={ShoppingBag} />
            <CardResumo label="Valor total" value={formatCurrency(total)} icon={DollarSign} />
            <CardResumo label="Média por cliente" value={formatCurrency(todos.length ? total / todos.length : 0)} icon={TrendingUp} />
          </div>
          <div className="bg-card rounded-xl border overflow-hidden">
            <div className="px-5 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-base font-bold">Todos os clientes ({todos.length})</h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar cliente..." className="pl-9 h-9 w-64" />
              </div>
            </div>
            <div className="overflow-x-auto">
              {lista.length ? <Tabela lista={lista} total={total} aoAbrir={abrir} />
                : <p className="py-10 text-center text-muted-foreground text-xs">Nenhum cliente bate com "{busca}"</p>}
            </div>
          </div>
        </>
          )}
        </Esmaecer>
      )}
    </div>
  );
}
